import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import api from "@/lib/axios"
import { ParentKomponenPenilaian } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: ParentKomponenPenilaian) => {
  const response = await api.post(
    "/operator-pasca/komponen-penilaian/parent-komponen-store",
    newData
  )
  return response.data
}

const patchData = async (newData: ParentKomponenPenilaian) => {
  const response = await api.patch(
    "/operator-pasca/komponen-penilaian/parent-komponen-update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_parent_komponen_penilaian_pasca: string
}) => {
  const response = await api({
    url: "/operator-pasca/master/komponen-penilaian/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: ParentKomponenPenilaian | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<ParentKomponenPenilaian>({
    id_ref_parent_komponen_penilaian_pasca:
      selectedRow?.id_ref_parent_komponen_penilaian_pasca || "",
    kode: selectedRow?.kode || "",
    nama_ref_parent_komponen: selectedRow?.nama_ref_parent_komponen || "",
    keterangan: selectedRow?.keterangan || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listParentKomponenPenilaian"],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete "
          description={`Apakah anda yakin akan menghapus "${""}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data" : "Update Data"}
      />
      <div>
        <Label>Kode</Label>
        <Input
          placeholder="Ketik kode..."
          value={form.kode}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, kode: event.target.value }))
          }}
        />
      </div>
      <div>
        <Label>Nama Parent Komponen</Label>
        <Input
          placeholder="Ketik nama parent komponen..."
          value={form.nama_ref_parent_komponen}
          onChange={(event) => {
            setForm((prev) => ({
              ...prev,
              nama_ref_parent_komponen: event.target.value,
            }))
          }}
        />
      </div>
      <div>
        <Label>Keterangan</Label>
        <Textarea
          placeholder="Ketik keterangan..."
          className="mt-1"
          value={form.keterangan}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              keterangan: event.target.value,
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
